import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { Connections, ConnectionView, Tokens } from './AccountConnections'
import { AccountDevice } from './AccountDevice'
import { AccountFeatures } from './AccountFeatures'
import { Place } from './AccountNotifications'
import { Password } from './AccountPassword'
import { Sessions } from './AccountSessions'
import type { FootAction } from './index'
import {
  NarrowingView,
  type NarrowTarget,
  NotificationChoices,
} from './NotificationChoices'
import { Sheet } from './Sheet'
import './account-sheet.css'

export type AccountTab =
  | 'signin'
  | 'studio'
  | 'notifications'
  | 'sessions'
  | 'device'
  | 'connections'

const TABS = [
  { key: 'signin', label: 'Sign in' },
  { key: 'studio', label: 'Studio' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'device', label: 'This computer' },
  { key: 'connections', label: 'Connections' },
] as const satisfies readonly { key: AccountTab; label: string }[]

/**
 * A person's own account, as distinct from any org's settings and from any
 * product's.
 *
 * What lives here is true of the PERSON — it follows them between
 * congregations, and now between products, and belongs to none of them. So the
 * shell draws it rather than a route: filed under a product it would say the
 * account belonged to that product, and there would be one of it per app.
 *
 * Putting connected agents in Settings would have filed them under whichever
 * org happened to be selected, which is exactly the confusion a grant must not
 * carry: an agent reaches the spaces somebody lent it, not the one they were
 * looking at.
 *
 * A Sheet with tabs, because this is one record with areas — the same rule
 * every other record here follows.
 */
export function AccountSheet({
  onClose,
  tab: firstTab = 'signin',
}: {
  onClose: () => void
  /** The area it opens on — Notifications, from the link in every notice. */
  tab?: AccountTab
}) {
  const [tab, setTab] = useState<AccountTab>(firstTab)
  /* A topic's narrowing is open, in place of the tabs, with its commit in the
     foot: the same navigation within the sheet a connection's view takes. */
  const [narrowing, setNarrowing] = useState<NarrowTarget | null>(null)
  const [foot, setFoot] = useState<FootAction | undefined>(undefined)
  /* Where the keyboard goes back to: the Change button the view was opened
     from, which the list draws again as the view closes. */
  const [returnTo, setReturnTo] = useState<string | null>(null)
  const closeNarrowing = useCallback(() => {
    if (narrowing) setReturnTo(`${narrowing.spaceId}|${narrowing.topic.key}`)
    setNarrowing(null)
    setFoot(undefined)
  }, [narrowing])
  useEffect(() => {
    if (!returnTo) return
    document
      .querySelector<HTMLElement>(`[data-narrow="${CSS.escape(returnTo)}"]`)
      ?.focus()
    setReturnTo(null)
  }, [returnTo])
  /*
   * A connection's own view is open. The sheet NAVIGATES WITHIN ITSELF rather
   * than stacking a panel on top: a panel over a panel covers the very list
   * that says what this connection's reach means — which of your spaces it
   * touches is read against the ones it does not. So the tabs step aside while
   * the view is up, and return when it closes.
   */
  const [editing, setEditing] = useState<string | null>(null)

  const trpc = useTRPC()
  const connections = useQuery(trpc.auth.connections.queryOptions())
  const me = useQuery(trpc.auth.me.queryOptions())
  const spaces = useQuery(trpc.space.mine.queryOptions())
  const open = (connections.data ?? []).find((c) => c.id === editing) ?? null

  return (
    <Sheet
      title={
        narrowing
          ? `${narrowing.topic.label} at ${narrowing.orgName}`
          : open
            ? open.clientName
            : 'Your account'
      }
      ariaLabel="Your account"
      onClose={onClose}
      tabs={
        narrowing || open
          ? undefined
          : { items: TABS, active: tab, onChange: setTab }
      }
      primary={narrowing ? foot : undefined}
      secondary={
        narrowing ? { label: 'Back', onSelect: closeNarrowing } : undefined
      }
    >
      {narrowing ? (
        <NarrowingView
          key={`${narrowing.spaceId}|${narrowing.topic.key}`}
          target={narrowing}
          onDone={closeNarrowing}
          onFoot={setFoot}
        />
      ) : open ? (
        <ConnectionView
          connection={open}
          spaces={spaces.data ?? []}
          canLendInstall={me.data?.isPlatformAdmin ?? false}
          onBack={() => setEditing(null)}
        />
      ) : tab === 'signin' ? (
        <Password />
      ) : tab === 'studio' ? (
        <AccountFeatures />
      ) : tab === 'notifications' ? (
        <>
          <NotificationChoices onNarrow={setNarrowing} />
          <Place />
        </>
      ) : tab === 'sessions' ? (
        <Sessions />
      ) : tab === 'device' ? (
        <AccountDevice />
      ) : (
        <>
          <Connections onEdit={setEditing} spaces={spaces.data ?? []} />
          <Tokens />
        </>
      )}
    </Sheet>
  )
}
